import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  Typography,
  Avatar,
  Box,
  Chip,
} from '@mui/material';
import {
  Facebook,
  Instagram,
  LinkedIn,
  Twitter as XIcon,
  YouTube,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Custom Tumblr icon component
const TumblrIcon = ({ sx }) => (
  <Typography 
    variant="h6" 
    sx={{ 
      color: '#36465d', 
      fontWeight: 'bold',
      ...sx 
    }}
  >
    T
  </Typography>
);

const platformStyles = {
  facebook: {
    icon: <Facebook sx={{ color: '#1877f2' }} />,
    avatarBg: '#1877f2',
    name: 'Facebook',
    cardStyle: { border: '1px solid #e4e6ea', backgroundColor: '#ffffff' },
  },
  x: {
    icon: <XIcon sx={{ color: '#000000' }} />,
    avatarBg: '#000',
    name: 'X (Twitter)',
    cardStyle: { border: '1px solid #e1e8ed', backgroundColor: '#ffffff' },
  },
  twitter: {
    icon: <XIcon sx={{ color: '#1da1f2' }} />,
    avatarBg: '#1da1f2',
    name: 'Twitter',
    cardStyle: { border: '1px solid #e1e8ed', backgroundColor: '#ffffff' },
  },
  instagram: {
    icon: <Instagram sx={{ color: '#E1306C' }} />,
    avatarBg: 'linear-gradient(45deg, #f58529, #dd2a7b, #8134af)',
    name: 'Instagram',
    cardStyle: { border: '1px solid #dbdbdb', backgroundColor: '#ffffff' },
  },
  linkedin: {
    icon: <LinkedIn sx={{ color: '#0077b5' }} />,
    avatarBg: '#0077b5',
    name: 'LinkedIn',
    cardStyle: { border: '1px solid #e0e0e0', backgroundColor: '#ffffff' },
  },
  youtube: {
    icon: <YouTube sx={{ color: '#ff0000' }} />,
    avatarBg: '#ff0000',
    name: 'YouTube',
    cardStyle: { border: '1px solid #e5e5e5', backgroundColor: '#ffffff' },
  },
  tumblr: {
    icon: <TumblrIcon sx={{ color: '#36465d' }} />,
    avatarBg: '#36465d',
    name: 'Tumblr',
    cardStyle: { border: '1px solid #e1e1e1', backgroundColor: '#ffffff' },
  },
};

const StyledCard = styled(Card)(({ theme, platform }) => {
  const style =
    platformStyles[platform]?.cardStyle || platformStyles.facebook.cardStyle;
  return {
    maxWidth: 400,
    minHeight: 220,
    borderRadius: platform === 'instagram' ? 8 : 12,
    boxShadow: theme.shadows[2],
    ...style,
    '& .MuiCardHeader-root': {
      paddingBottom: platform === 'instagram' ? 8 : 16,
    },
    '& .MuiCardContent-root': {
      paddingTop: platform === 'instagram' ? 0 : 8,
    },
  };
});

const StyledAvatar = styled(Avatar)(({ platform }) => ({
  backgroundColor:
    platformStyles[platform]?.avatarBg || platformStyles.facebook.avatarBg,
  background:
    platformStyles[platform]?.avatarBg || platformStyles.facebook.avatarBg,
  width: 40,
  height: 40,
}));

function PostPreview({
  platform = 'facebook',
  content = '',
  media = '',
  title = '',
  user = {},
  hashtags = [],
  style = {},
}) {
  const p = platformStyles[platform] || platformStyles.facebook;
  const platformKey = platform.toLowerCase();

  const renderHashtags = () => {
    if (!hashtags || hashtags.length === 0) return null;

    return (
      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {hashtags.slice(0, 5).map((tag, index) => (
          <Chip
            key={index}
            label={`#${tag}`}
            size="small"
            sx={{
              fontSize: '0.75rem',
              height: 20,
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        ))}
        {hashtags.length > 5 && (
          <Typography variant="caption" color="text.secondary">
            +{hashtags.length - 5} more
          </Typography>
        )}
      </Box>
    );
  };

  const renderMedia = () => {
    if (!media) return null;

    const isVideo = media.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i);
    const isImage = media.match(/\.(jpg|jpeg|png|gif|webp)$/i);

    if (isVideo) {
      return (
        <Box sx={{ px: 2, pb: 2 }}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: '#f0f0f0',
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Video Preview
            </Typography>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" color="white">
                ▶
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    if (isImage) {
      return (
        <Box sx={{ px: 2, pb: 2 }}>
          <CardMedia
            component="img"
            image={media}
            alt="Post media"
            sx={{
              borderRadius: platform === 'instagram' ? 8 : 2,
              maxHeight: 200,
              objectFit: 'cover',
              width: '100%',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </Box>
      );
    }

    return null;
  };

  return (
    <StyledCard platform={platformKey} sx={style}>
      <CardHeader
        avatar={<StyledAvatar platform={platformKey}>{p.icon}</StyledAvatar>}
        title={
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user.name || p.name}
          </Typography>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            {user.handle ? `@${user.handle}` : p.name}
          </Typography>
        }
        sx={{
          '& .MuiCardHeader-content': {
            minWidth: 0,
          },
        }}
      />
      {title && platform === 'youtube' && (
        <Typography
          variant="h6"
          sx={{
            px: 2,
            fontWeight: 600,
            fontSize: '1.1rem',
            lineHeight: 1.3,
            mb: 1,
          }}
        >
          {title}
        </Typography>
      )}
      <CardContent sx={{ pt: platform === 'instagram' ? 0 : 1, pb: 1 }}>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-line',
            lineHeight: 1.4,
            fontSize:
              platform === 'twitter' || platform === 'x'
                ? '0.9rem'
                : '0.875rem',
          }}
        >
          {content}
        </Typography>
        {renderHashtags()}
      </CardContent>
      {renderMedia()}
    </StyledCard>
  );
}

export default PostPreview;
